import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { gql } from "apollo-boost";
import { useQuery } from "@apollo/react-hooks";
import { Spin, Button, Table } from "antd";
import { User } from "../../lib/types";
import _ from "lodash";
import fromnow from "fromnow";
import {
  SCSSCBYRULERPLAYER as SCSSCBYRULERPLAYERData,
  SCSSCBYRULERPLAYERVariables,
} from "./__generated__/SCSSCBYRULERPLAYER";
import {
  SCSRULER as SCSRULERData,
  SCSRULERVariables,
} from "./__generated__/SCSRULER";

const SCSSCBYRULERPLAYER = gql`
  query SCSSCBYRULERPLAYER($ruler: Int!, $player: Int!) {
    scByRulerPlayer(ruler: $ruler, player: $player) {
      sc
      x
      y
      z
      tx
      ty
      tz
      orbit
      type
      scIdentity {
        ruler
        number
        name
      }
      updated
    }
  }
`;

const SCSRULER = gql`
  query SCSRULER($ruler: [Int!]!) {
    ruler(ruler: $ruler) {
      homeX
      homeY
      homeZ
    }
  }
`;

interface Props {
  user: User;
}

export const SCSPage = ({ user, history }: Props & RouteComponentProps) => {
  const ruler = user["http://empyreanchallenge.com/app_metadata"].ruler;
  const player = user["http://empyreanchallenge.com/app_metadata"].player;
  const { data, loading, error } = useQuery<
    SCSSCBYRULERPLAYERData,
    SCSSCBYRULERPLAYERVariables
  >(SCSSCBYRULERPLAYER, {
    variables: {
      ruler,
      player,
    },
    fetchPolicy: "network-only",
  });

  const rulers = useQuery<SCSRULERData, SCSRULERVariables>(SCSRULER, {
    variables: { ruler: [ruler] },
  });

  if (loading || rulers.loading) {
    return <Spin size="large" />;
  }

  if (error || rulers.error) {
    if (error) {
      console.log(error);
    }
    if (rulers.error) {
      console.log(rulers.error);
    }
    return <h2>Error Loading Data!</h2>;
  }

  let scs = data && data.scByRulerPlayer ? data.scByRulerPlayer : [];
  const myRuler = rulers.data ? rulers.data.ruler[0] : 0;

  var children: JSX.Element[] = [];

  const handleDetail = async (sc: string) => {
    history.push(`/sc/${sc}`);
  };

  let normalized = _.map(scs, (value) => {
    const myIdentity = _.find(value.scIdentity, ["ruler", ruler]);
    return {
      x: myRuler ? value.x - myRuler.homeX : value.x,
      y: myRuler ? value.y - myRuler.homeY : value.y,
      z: myRuler ? value.z - myRuler.homeZ : value.z,
      tx: value.tx,
      ty: value.ty,
      tz: value.tz,
      updated: value.updated,
      name: myIdentity ? myIdentity.name : "",
      number: myIdentity ? myIdentity.number : 0,
      sc: value.sc,
      type: value.type,
      orbit: value.orbit,
    };
  });

  const sortFunction = (a: any, b: any) => {
    if (a.x === b.x) {
      if (a.y === b.y) {
        if (a.z === b.z) {
          if (a.orbit === b.orbit) {
            return a.number - b.number;
          } else {
            return a.orbit < b.orbit ? -1 : 1;
          }
        } else {
          return a.z - b.z;
        }
      } else {
        return a.y - b.y;
      }
    } else {
      return a.x - b.x;
    }
  };

  const sorted = normalized.sort(
    sortFunction
    /*            
                        a.x === b.x
                            ? a.y === b.y
                                ? a.z === b.z
                                    ? a.number - b.number
                                    : a.z - b.z
                                : a.y - b.y
                            : a.x - b.x
                        )
            */
  );

  const columns = [
    {
      title: <p style={{ textAlign: "center" }}>SC</p>,
      dataIndex: "sc",
      key: "sc",
      render: (value: any) => (
        <Button block onClick={() => handleDetail(value.detail)}>
          {value.sc}
        </Button>
      ),
    },
    {
      title: <p style={{ textAlign: "center" }}>X</p>,
      dataIndex: "x",
      key: "x",
      render: (text: string) => <p style={{ textAlign: "center" }}>{text}</p>,
    },
    {
      title: <p style={{ textAlign: "center" }}>Y</p>,
      dataIndex: "y",
      key: "y",
      render: (text: string) => <p style={{ textAlign: "center" }}>{text}</p>,
    },
    {
      title: <p style={{ textAlign: "center" }}>Z</p>,
      dataIndex: "z",
      key: "z",
      render: (text: string) => <p style={{ textAlign: "center" }}>{text}</p>,
    },
    {
      title: <p style={{ textAlign: "center" }}>Orbit</p>,
      dataIndex: "orbit",
      key: "orbit",
      render: (text: string) => <p style={{ textAlign: "center" }}>{text}</p>,
    },
    {
      title: <p style={{ textAlign: "center" }}>Tactical</p>,
      dataIndex: "tactical",
      key: "tactical",
      render: (text: string) => <p style={{ textAlign: "center" }}>{text}</p>,
    },
    {
      title: <p style={{ textAlign: "center" }}>Last Update</p>,
      dataIndex: "update",
      key: "update",
      render: (text: string) => <p style={{ textAlign: "center" }}>{text}</p>,
    },
  ];

  let SCsData: any = [];

  sorted.forEach((q) => {
    SCsData.push(
      _.assign({
        key: q.sc,
        sc: {
          sc: `${q.name} (${q.type} ${q.number})`,
          detail: q.sc,
        },
        x: q.x.toFixed(1),
        y: q.y.toFixed(1),
        z: q.z.toFixed(1),
        orbit: q.orbit,
        tactical: `${q.tx.toFixed(0)} / ${q.ty.toFixed(0)} / ${q.tz.toFixed(
          0
        )}`,
        update: fromnow(q.updated, {
          suffix: true,
          max: 3,
          zero: false,
          and: true,
        }),
      })
    );
  });

  children.push(
    <Table
      key="SCsTable"
      size="middle"
      dataSource={SCsData}
      columns={columns}
    />
  );

  return <div>{children}</div>;
};
